import * as THREE from "three";
import type { FragColorRule, FragFilter, LoadedModel } from "./types";

export const getIfcTypeRegexes = (ifcTypes: string[]) =>
  ifcTypes.map((type) => new RegExp(`^${type}$`, "i"));

export async function applyVisibilityFilterToModel(
  entry: LoadedModel,
  filter: FragFilter | null
) {
  if (filter === null) {
    await entry.model.resetVisible();
    return;
  }

  const hiddenIfcTypes = filter.hiddenIfcTypes?.filter(Boolean) ?? [];
  const hasOnlyHiddenIfcTypes =
    hiddenIfcTypes.length > 0 &&
    !filter.ifcTypes?.length &&
    !filter.guids?.length &&
    !filter.zSpan;

  if (hasOnlyHiddenIfcTypes) {
    await entry.model.resetVisible();
    const hiddenIds = await entry.model.getItemsByQuery({
      categories: getIfcTypeRegexes(hiddenIfcTypes),
    });
    if (hiddenIds.length) await entry.model.setVisible(hiddenIds, false);
    return;
  }

  let ids: Set<number> = new Set(
    await entry.model.getItemsByQuery({ categories: [/.*/] })
  );

  if (filter.ifcTypes?.length) {
    const typeIds = new Set(
      await entry.model.getItemsByQuery({
        categories: getIfcTypeRegexes(filter.ifcTypes),
      })
    );
    ids = new Set([...ids].filter((id) => typeIds.has(id)));
  }

  if (hiddenIfcTypes.length) {
    const hiddenTypeIds = new Set(
      await entry.model.getItemsByQuery({
        categories: getIfcTypeRegexes(hiddenIfcTypes),
      })
    );
    ids = new Set([...ids].filter((id) => !hiddenTypeIds.has(id)));
  }

  if (filter.guids?.length) {
    const localIds = (await entry.model.getLocalIdsByGuids(filter.guids)).filter(
      (id): id is number => id != null
    );
    const guidSet = new Set(localIds);
    ids = new Set([...ids].filter((id) => guidSet.has(id)));
  }

  if (filter.zSpan && ids.size > 0) {
    const { min, max } = filter.zSpan;
    const positions = await entry.model.getPositions([...ids]);
    const filtered = new Set<number>();
    let i = 0;
    for (const id of ids) {
      const pos = positions[i++];
      if (pos && pos.y >= min && pos.y <= max) filtered.add(id);
    }
    ids = filtered;
  }

  await entry.model.setVisible(undefined, false);
  if (ids.size > 0) await entry.model.setVisible([...ids], true);
}

export async function applyVisibilityFilterToModels(
  loadedModels: Map<string, LoadedModel>,
  filter: FragFilter | null
) {
  for (const entry of loadedModels.values()) {
    await applyVisibilityFilterToModel(entry, filter);
  }
}

export async function applyColorRulesToModels(
  loadedModels: Map<string, LoadedModel>,
  colorRules?: FragColorRule[]
) {
  for (const entry of loadedModels.values()) {
    await entry.model.resetColor(undefined);
  }
  if (!colorRules?.length) return;

  for (const entry of loadedModels.values()) {
    for (const rule of colorRules) {
      let ids: number[] = [];
      if (rule.ifcTypes?.length) {
        ids.push(
          ...(await entry.model.getItemsByQuery({
            categories: getIfcTypeRegexes(rule.ifcTypes),
          }))
        );
      }
      if (rule.guids?.length) {
        ids.push(
          ...(await entry.model.getLocalIdsByGuids(rule.guids)).filter(
            (id): id is number => id != null
          )
        );
      }
      if (ids.length) {
        await entry.model.setColor([...new Set(ids)], new THREE.Color(rule.color));
      }
    }
  }
}
